import React, { useCallback, useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import FromField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/FromField'
import StopField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/StopField'
import ToField from 'modules/expenses/components/expenses/Detail/_newForm/Blocks/Trip/ToField'
import { CustomizationProps } from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import { PermissionsProps } from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { convertToNumber } from 'modules/expenses/lib/numberFormating'
import { calculateDistance } from 'modules/locations/hooks/useAutocompleteAddress'

interface Props extends PermissionsProps, CustomizationProps {
  setIsCalculatingDistance?: (loading: boolean) => void
  hasMileageMultipleStops?: boolean
}

interface Place {
  lat: number
  lng: number
}

interface StopFormValue {
  name?: string | null
  latitude?: string | null
  longitude?: string | null
}

const Trip = (props: Props) => {
  const { setValue, watch } = useFormContext()
  const { setIsCalculatingDistance, hasMileageMultipleStops = false } = props

  // Track if we're initializing with existing data
  const isInitializedRef = useRef(false)
  const hasExistingData = Boolean(props.expensable?.id)

  // Watch form values
  const originLatitude = watch('originLatitude')
  const originLongitude = watch('originLongitude')
  const destinationLatitude = watch('destinationLatitude')
  const destinationLongitude = watch('destinationLongitude')
  const units = watch('units')
  const stops: StopFormValue[] = watch('stops') || []

  const roundTrip = watch('roundTrip')

  const recalculateDistance = useCallback(async () => {
    if (!destinationLatitude || !destinationLongitude || !originLatitude || !originLongitude) {
      return
    }

    if (!setIsCalculatingDistance) {
      return
    }

    setIsCalculatingDistance(true)

    try {
      // Collect valid intermediate stops with coordinates
      const validStops = stops.filter(
        (s: StopFormValue): s is StopFormValue & { latitude: string; longitude: string } =>
          Boolean(s.latitude && s.longitude)
      )

      let totalDistance: number | undefined

      if (validStops.length > 0) {
        // Build ordered list of points: origin → stop1 → stop2 → ... → destination
        const points = [
          { lat: originLatitude, lng: originLongitude },
          ...validStops.map((s) => ({ lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) })),
          { lat: destinationLatitude, lng: destinationLongitude }
        ]

        // Calculate distance for each consecutive leg using the same method as origin → destination
        const legDistances = await Promise.all(
          points.slice(0, -1).map((point, i) => {
            const nextPoint = points[i + 1]
            if (!nextPoint) return Promise.resolve(undefined)
            return calculateDistance({
              latOrigin: point.lat,
              lonOrigin: point.lng,
              latDestination: nextPoint.lat,
              lonDestination: nextPoint.lng,
              roundTrip: false,
              units
            })
          })
        )

        totalDistance = legDistances.reduce((sum, d) => sum + (d ? convertToNumber(d) : 0), 0)
      } else {
        // We are ignoring the roundtrip during the request
        // We'll apply it after the request done so that calculatedMileage represents
        // the distance from origin and destination without roundtrip, as this causes
        // inconsistencies for the mileage alert.
        const distance = await calculateDistance({
          latOrigin: originLatitude,
          lonOrigin: originLongitude,
          latDestination: destinationLatitude,
          lonDestination: destinationLongitude,
          roundTrip: false,
          units
        })

        totalDistance = distance ? convertToNumber(distance) : undefined
      }

      if (totalDistance) {
        // Handles if roundtrip is enabled when the user checks
        if (roundTrip) {
          setValue('mileage', totalDistance * 2, { shouldValidate: true, shouldDirty: true })
        } else {
          setValue('mileage', totalDistance, { shouldValidate: true, shouldDirty: true })
        }
        // Keeps the calculated distance to just match the distance from origin and destination without roundtrip.
        setValue('calculatedMileage', totalDistance, { shouldValidate: true, shouldDirty: true })
      }
    } finally {
      setIsCalculatingDistance(false)
    }
  }, [
    originLatitude,
    originLongitude,
    destinationLatitude,
    destinationLongitude,
    units,
    stops,
    roundTrip,
    setValue,
    setIsCalculatingDistance
  ])

  // Serialized stops coords for dependency tracking in useEffect
  const stopsCoordsKey = stops
    .map((s: StopFormValue) => `${s.latitude ?? ''},${s.longitude ?? ''}`)
    .join('|')

  // Watch for changes in coordinates and recalculate distance when both sets are available
  useEffect(() => {
    // Skip initial calculation when editing existing data

    if (hasExistingData && !isInitializedRef.current) {
      isInitializedRef.current = true
      return
    }

    if (originLatitude && destinationLatitude) {
      recalculateDistance()
    }
  }, [originLatitude, destinationLatitude, units, hasExistingData, stopsCoordsKey])

  const handleFromPlaceSelected = useCallback(
    (place: Place) => {
      setValue('originLatitude', String(place.lat), { shouldValidate: true, shouldDirty: true })
      setValue('originLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true
      })
    },
    [setValue]
  )

  const handleToPlaceSelected = useCallback(
    (place: Place) => {
      setValue('destinationLatitude', String(place.lat), {
        shouldValidate: true,
        shouldDirty: true
      })
      setValue('destinationLongitude', String(place.lng), {
        shouldValidate: true,
        shouldDirty: true
      })
    },
    [setValue]
  )

  const handleStopPlaceSelected = useCallback(
    (index: number, place: Place) => {
      setValue(`stops.${index}.latitude`, String(place.lat), {
        shouldValidate: true,
        shouldDirty: true
      })
      setValue(`stops.${index}.longitude`, String(place.lng), {
        shouldValidate: true,
        shouldDirty: true
      })
    },
    [setValue]
  )

  const handleAddStop = useCallback(() => {
    const current: StopFormValue[] = watch('stops') || []
    const next = [...current, { name: '', latitude: null, longitude: null }]
    setValue('stops', next, { shouldDirty: true })
  }, [watch, setValue])

  const handleRemoveStop = useCallback(
    (index: number) => {
      const current: StopFormValue[] = watch('stops') || []
      const next = current.filter((_, i) => i !== index)
      setValue('stops', next, { shouldDirty: true })
    },
    [watch, setValue]
  )

  return (
    <Box gap="s16">
      <FromField {...props} fieldIdentifier="from" onPlaceSelected={handleFromPlaceSelected} />

      {hasMileageMultipleStops &&
        stops.map((_stop: StopFormValue, index: number) => (
          <Box key={index} flexDirection="row" gap="s8" alignItems="center" width="full">
            <Box flexGrow width="full">
              <StopField
                {...props}
                fieldIdentifier="stops"
                index={index}
                onPlaceSelected={(place) => handleStopPlaceSelected(index, place)}
              />
            </Box>
            <Box paddingTop="s24">
              <Button type="secondary" onClick={() => handleRemoveStop(index)} iconName="delete" />
            </Box>
          </Box>
        ))}

      {hasMileageMultipleStops && (
        <Button type="secondary" onClick={handleAddStop}>
          {i18n.t('expenses_v2.forms.fields.stop.add')}
        </Button>
      )}

      <ToField {...props} fieldIdentifier="to" onPlaceSelected={handleToPlaceSelected} />
    </Box>
  )
}

export default Trip
